import { Fragment } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Divider, Typography } from "@mui/material"
import { faCalendar, faLocationDot, faPenToSquare } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardButton } from "../../../../buttons/StandardButton"

import type { TakeawayCartItem, TakeawayKitchen } from "../takeawayTypes"
import { TakeawayCheckoutItemRow } from "./TakeawayCheckoutItemRow"

const useStyles = makeStyles()((theme) => ({
    card: {
        background: theme.palette.tailwind.blue[50],
        borderRadius: "1.5rem",
        display: "flex",
        flexDirection: "column",
    },
    cardHeader: {
        flexShrink: 0,
        padding: "2rem 2rem 3rem",
    },
    summaryHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: ".75rem",
    },
    summaryTitleText: {
        fontSize: "2rem",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[950],
    },
    correctOrderBtn: {
        color: theme.palette.tailwind.blue[500],
        padding: 0,
        minWidth: 0,
        flexShrink: 0,
        fontSize: "1rem",
    },
    kitchenBanner: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    kitchenBannerIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[500],
        flexShrink: 0,
    },
    kitchenBannerText: {
        color: theme.palette.tailwind.slate[500],
    },
    cardScroll: {
        padding: "3rem 2rem 0",
    },
    dateGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    groupDivider: {
        margin: "3rem 0",
    },
    dateRow: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    dateIconBox: {
        width: "1rem",
        height: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    dateIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[500],
        flexShrink: 0,
    },
    dateLabel: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[950],
    },
    dateTag: {
        background: theme.palette.tailwind.slate[200],
        borderRadius: ".375rem",
        padding: "0 .375rem",
        color: theme.palette.tailwind.slate[950],
    },
    groupItems: {
        display: "flex",
        flexDirection: "column",
    },
    itemDivider: {
        borderColor: theme.palette.tailwind.slate[200],
        margin: ".875rem 0",
    },
    headerDivider: {
        margin: "0 2rem",
    },
    bottomDivider: {
        marginTop: "3rem",
    },
    totalWrap: {
        flexShrink: 0,
        padding: "1rem 2rem",
    },
    totalRow: {
        display: "flex",
        alignItems: "baseline",
        gap: "1.5rem",
        marginBottom: "0.5rem",
    },
    totalLabel: {
        flex: 1,
        textAlign: "right",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[700],
    },
    totalAmount: {
        minWidth: "11.25rem",
        textAlign: "right",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[950],
    },
    vatRow: {
        display: "flex",
        alignItems: "baseline",
        gap: "1.5rem",
    },
    vatLabel: {
        flex: 1,
        textAlign: "right",
        color: theme.palette.tailwind.slate[500],
    },
    vatAmount: {
        minWidth: "11.25rem",
        textAlign: "right",
        fontWeight: 700,
        color: theme.palette.tailwind.slate[500],
    },
}))

type TakeawayCheckoutOrderSummaryProps = Readonly<{
    cartByDate: Record<string, TakeawayCartItem[]>
    sortedDates: string[]
    kitchen: TakeawayKitchen
    moneyFactory: ReturnType<typeof useMoney>
    totalPrice: ReturnType<typeof toDinero>
    vatAmount: ReturnType<typeof toDinero>
    onCorrectOrder: () => void
}>

export function TakeawayCheckoutOrderSummary({
    cartByDate,
    sortedDates,
    kitchen,
    moneyFactory,
    totalPrice,
    vatAmount,
    onCorrectOrder,
}: TakeawayCheckoutOrderSummaryProps) {
    const { classes } = useStyles()
    const dt = useDateTime()

    const kitchenBannerLabel = kitchen.webshop?.name ? `${kitchen.webshop.name} - ${kitchen.name}` : kitchen.name

    return (
        <Box className={classes.card}>
            <Box className={classes.cardHeader}>
                <Box className={classes.summaryHeader}>
                    <LocalizedStrict id="takeaway-checkout-order-summary-title">
                        <Typography className={classes.summaryTitleText}>Order summary</Typography>
                    </LocalizedStrict>
                    <StandardButton
                        variant="text"
                        color="primary"
                        fullWidth={false}
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                        className={classes.correctOrderBtn}
                        onClick={onCorrectOrder}
                    >
                        <LocalizedStrict id="takeaway-confirm-order-correct-order">
                            <span>Correct order</span>
                        </LocalizedStrict>
                    </StandardButton>
                </Box>

                <Box className={classes.kitchenBanner}>
                    <FontAwesomeIcon icon={faLocationDot} className={classes.kitchenBannerIcon} />
                    <Typography variant="body1" className={classes.kitchenBannerText}>
                        {kitchenBannerLabel}
                    </Typography>
                </Box>
            </Box>

            <Divider className={classes.headerDivider} />

            <Box className={classes.cardScroll}>
                {sortedDates.map((dateKey, index) => {
                    const items = cartByDate[dateKey] ?? []
                    return (
                        <Fragment key={dateKey}>
                            {index > 0 && <Divider className={classes.groupDivider} />}
                            <Box className={classes.dateGroup}>
                                <Box className={classes.dateRow}>
                                    <Box className={classes.dateIconBox}>
                                        <FontAwesomeIcon icon={faCalendar} className={classes.dateIcon} />
                                    </Box>
                                    <LocalizedStrict id="takeaway-checkout-order-summary-delivery-label">
                                        <Typography variant="body1" className={classes.dateLabel}>
                                            Delivery:
                                        </Typography>
                                    </LocalizedStrict>
                                    <Typography variant="body1" className={classes.dateTag}>
                                        {dt.fromISO(dateKey).toFormat("d MMMM yyyy")}
                                    </Typography>
                                </Box>
                                <Box className={classes.groupItems}>
                                    {items.map((item, i) => (
                                        <Box key={item.product.id}>
                                            {i > 0 && <Divider className={classes.itemDivider} />}
                                            <TakeawayCheckoutItemRow item={item} moneyFactory={moneyFactory} />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Fragment>
                    )
                })}
            </Box>

            <Divider className={classes.bottomDivider} />

            <Box className={classes.totalWrap}>
                <Box className={classes.totalRow}>
                    <LocalizedStrict id="takeaway-checkout-total-label">
                        <Typography variant="body1" className={classes.totalLabel}>
                            Total
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="h4" className={classes.totalAmount}>
                        {moneyFactory.format(totalPrice)}
                    </Typography>
                </Box>
                <Box className={classes.vatRow}>
                    <LocalizedStrict id="takeaway-checkout-vat-label">
                        <Typography variant="subtitle1" className={classes.vatLabel}>
                            VAT (25%)
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="body1" className={classes.vatAmount}>
                        {moneyFactory.format(vatAmount)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}
